import { useCallback, useEffect, useRef, useState } from 'react'

type ApproxLocationStatus = 'idle' | 'requesting_gps' | 'reverse_geocoding' | 'ready' | 'error'

export interface ApproxLocationState {
  status: ApproxLocationStatus
  city: string | null
  state: string | null
  error: string | null
  request: () => void
  reset: () => void
}

function normalizeCity(value: string | null | undefined) {
  const v = (value || '').trim()
  return v.length > 0 ? v : null
}

function normalizeState(value: string | null | undefined) {
  const v = (value || '').trim()
  if (!v) return null
  // Prefer UF like "SP"
  if (v.length === 2) return v.toUpperCase()
  return v
}

export function useApproxLocation(): ApproxLocationState {
  const [status, setStatus] = useState<ApproxLocationStatus>('idle')
  const [city, setCity] = useState<string | null>(null)
  const [state, setState] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mountedRef = useRef(true)
  const requestIdRef = useRef(0)

  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  const reset = useCallback(() => {
    requestIdRef.current += 1
    setStatus('idle')
    setCity(null)
    setState(null)
    setError(null)
  }, [])

  const request = useCallback(() => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    setError(null)

    if (!('geolocation' in navigator)) {
      setStatus('error')
      setError('Geolocalização não suportada neste navegador.')
      return
    }

    setStatus('requesting_gps')

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        if (!mountedRef.current) return
        if (requestIdRef.current !== requestId) return

        const lat = pos.coords.latitude
        const lng = pos.coords.longitude

        setStatus('reverse_geocoding')

        try {
          // Reverse geocode sem chave (BigDataCloud). Retorna cidade/UF aproximadas.
          const resp = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=pt`,
            { method: 'GET' }
          )

          if (!resp.ok) throw new Error(`Reverse geocode failed (${resp.status})`)

          const data: any = await resp.json()

          const resolvedCity =
            normalizeCity(data.city) ||
            normalizeCity(data.locality) ||
            normalizeCity(data.principalSubdivision) ||
            null

          let resolvedState: string | null =
            normalizeState(data.principalSubdivisionCode?.toString()?.split('-')?.slice(-1)?.[0]) ||
            normalizeState(data.principalSubdivision)

          setCity(resolvedCity)
          setState(resolvedState)
          setStatus('ready')
        } catch (e: any) {
          setStatus('error')
          setError(e?.message || 'Erro ao obter localização aproximada.')
        }
      },
      (err) => {
        if (!mountedRef.current) return
        if (requestIdRef.current !== requestId) return

        setStatus('error')
        if (err.code === err.PERMISSION_DENIED) {
          setError('Permissão de localização negada.')
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setError('Localização indisponível.')
        } else if (err.code === err.TIMEOUT) {
          setError('Tempo esgotado ao obter localização.')
        } else {
          setError('Erro ao obter localização.')
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 5 * 60 * 1000
      }
    )
  }, [])

  return { status, city, state, error, request, reset }
}

